import type { ISolutionListResponse } from '@eureka/shared/solution-card/src/types';
import type { EventData } from '../types/index';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { FactoryPsHttpSSE } from '@patsnap-utils/sse';
import {
    type PatSnapCommonResponseData,
    PsHttpService,
} from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface AiSearchFilter {
    resource?: string[]; // 数据源
    year?: [number, number] | [string]; // 发表时间(年)
    legal_status?: string[]; // 方案成熟度: ["verified", "new", "free"]
    country?: { name: string; include: boolean }; // 国家与地区
    company?: { name: string; include: boolean }; // 竞争对手
}

export interface AiSearchJobRequest {
    initial_message?: string;
    conversation_id?: string;
    filter?: AiSearchFilter;
}

export interface AiSearchJobResponse {
    session_id: string;
    conversation_id: string;
}

export interface StreamLoopMessageRequest {
    data: EventData[];
    status: 'running' | 'finish'
}

export interface FigureDocumentRequest {
    chart_type: string;
    chart_query: string;
    efq: string;
    limit: number;
    page: number;
}

export interface DocDocumentRequest {
    session_id: string;
    limit: number;
    page: number;
    solution_type: 'patent' | 'paper';
}

export interface FigureDataResponse {
    chart_type: 'apd_y' | 'paper_datetime_ts' | 'country' | 'company_analysis' | 'word_cluster' | 'key_benefit_phrase_adc_c' | 'ancs_key_benefit_phrase' | 'apd_y_ancs';
    chart_data?: any; // For table or other types
    chart_query: string;
    [key: string]: any; // Allow additional properties
}

export interface IAISearchSolutionListResponse extends ISolutionListResponse {
    application?: string;
}

export interface AiSearchApi {
    getRecommendCases(userInput: string, signal?: AbortSignal): Promise<{ recommend_cases: string[] }>;
    createAiSearchJob(data: AiSearchJobRequest): Promise<AiSearchJobResponse>;
    getJobStream(data: {
        conversation_id: string;
        session_id?: string;
    }): PsHttpSSE<EventData>;
    getAISearchStreamLoop(data: {
        conversation_id: string;
        session_id?: string;
    }): Promise<StreamLoopMessageRequest>;
    getFigureData(id: string, data: { chart_type: string, chart_id: string, query?: string }): Promise<FigureDataResponse>;
    getAllDocumentsData(data: DocDocumentRequest): Promise<IAISearchSolutionListResponse>;
    getDocumentsData(data: FigureDocumentRequest): Promise<ISolutionListResponse>;
}

export const AiSearchApi = Symbol('AiSearchApi');

@injectable()
export class AiSearchApiImpl extends PsHttpService implements AiSearchApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/api/research`,
            docBaseURL: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka`,
            headers: {
                'X-API-Version': '1.0',
            },
        };
    }

    /**
     * 问题联想
     */
    getRecommendCases(userInput: string, signal?: AbortSignal) {
        return this.post<PatSnapCommonResponseData<{ recommend_cases: string[] }>>({
            url: `${this.options.docBaseURL}/plg-home-page/plg-recommend-cases`,
            data: {
                user_input: userInput,
            },
            signal,
        }).then((res) => {
            return res.data as { recommend_cases: string[] };
        });
    }

    /**
     * 创建一个AI搜索任务
     */
    createAiSearchJob(data: AiSearchJobRequest) {
        return this.post<PatSnapCommonResponseData<AiSearchJobResponse>>({
            url: `${this.options.baseURL}/sessions`,
            data,
        }).then((res) => {
            return res.data as AiSearchJobResponse;
        });
    }

    @inject(FactoryPsHttpSSE)
    private _factorySsePsHttp!: FactoryPsHttpSSE;

    /**
     * 获取任务的流
     */
    getJobStream(data: {
        conversation_id: string;
        session_id?: string;
    }) {
        return this._factorySsePsHttp<EventData>()
            .init({
                url: `${this.options.baseURL}/conversations/${data.conversation_id}/stream`,
                data,
                hasFinalMessage: () => true,
            });
    }

    /**
     * sse降级轮询
     */
    getAISearchStreamLoop(data: AiSearchJobRequest) {
        return this.post<PatSnapCommonResponseData<StreamLoopMessageRequest>>({
            url: `${this.options.baseURL}/conversations/${data.conversation_id}/status`,
            data,
        }).then((res) => {
            return res.data as StreamLoopMessageRequest;
        });
    }

    /**
     * 获取图表/图片数据
     */
    getFigureData(id: string, data: { chart_type: string, chart_id: string, query?: string }) {
        return this.post<PatSnapCommonResponseData<FigureDataResponse>>({
            url: `/sessions/${id}/chart-data`,
            data,
        }).then((res) => {
            return res.data as FigureDataResponse;
        });
    }

    /**
     * 获取资料数据
     */
    getAllDocumentsData(data: DocDocumentRequest) {
        return this.post<PatSnapCommonResponseData<IAISearchSolutionListResponse>>({
            url: `/sessions/${data.session_id}/documents`,
            data: {
                session_id: data.session_id,
                solution_type: data.solution_type,
                limit: data.limit,
                page: data.page,
            },
        }).then((res) => {
            return res.data as IAISearchSolutionListResponse;
        });
    }

    /**
     * 获取图表/图片数据
     */
    getDocumentsData(data: FigureDocumentRequest) {
        return this.post<PatSnapCommonResponseData<ISolutionListResponse>>({
            url: `${this.options.docBaseURL}/agent/tech-graph/chart-documents`,
            data: {
                chart_type: data.chart_type,
                chart_query: data.chart_query,
                efq: data.efq,
                limit: data.limit,
                page: data.page,
            },
        }).then((res) => {
            return res.data as ISolutionListResponse;
        });
    }
}
